import { createSlice } from '@reduxjs/toolkit';
import { filterCloudEnvironments, filterProviders } from 'views/Login/helpers';
import {
  setShowScreen,
  setUsername,
  setPassword,
  passwordAndScreenUpdates,
  setForgotPasswordState,
  setIsInternal,
  setShowInvalidLink,
  login,
  lookupRegion,
  sso,
  forgotPassword,
  recoverPassword,
  verifyPassword,
  availability,
  disconnectCalendar,
  usersListInfo,
  fetchBackgroundEvents,
  changePassword,
  updateProfileSettings,
  updateAvailibility,
  updateBookingSettings,
  sessionInfo,
  fetchTokenInfo,
  fetchAddUser,
  fetchUpdateUser,
  fetchUserDetails,
  fetchAtsUser,
  importAtsUser,
  expirePassword,
  addUserInbox,
  removeUserInbox,
  removeLicense,
  addLicense,
  updateDisableUser,
  fetchDeleteUser,
  fetchMessageTemplate,
  saveMessageTemplateInbound,
  deleteMessageTemplate,
  setMessageTemplateIsEdittrue,
  resetMessageTemplateSaveStatus,
  fetchResetPassword,
  handleSnackClose,
  resetLoginError,
  resetAtsUser,
  resetProfileUpdateStatus,
  addAtsPersonId,
  removeAtsPersonId,
  resetUserSave,
  setAPIError,
  addFacebook,
  updateFacebook,
  disconnectFacebookUpdate,
  refreshFacebook,
  setIsRowsPerPageChanged,
  fetchSendAsUserDetails,
  resetSendAsUser,
  saveWhatsappConnectionResponse,
  verifyWhatsAppNumber,
  resetWSSBusinessData,
  disconnectWhatsAppBusinessAccount,
} from '../actions';
import {
  productTypes,
  roles,
  authTypes,
  calendarTypes,
  timeZones,
  extendedTimeZones,
  timeZoneMap,
  extendedTimeZoneMap,
  EU_HOSTED,
  backgroundEventTypes,
} from 'appConstants';
import moment from 'moment-timezone';
import { commonStateUpdates } from './commonStateUpdate';

export const initialState = {
  id: '',
  error: {},
  authorizing: false,
  authenticated: false,
  userSaved: false,
  rememberMe: false,
  userStatusUpdated: false,
  isInternal: false,
  cloudEnvironments: [],
  authProviders: [],
  remoteAuths: [],
  authType: authTypes.LEGACY,
  isLoadingRemoteAuths: false,
  customerIds: [],
  inboxAccessList: [],
  inboxAccessListWithThreads: [],
  canAddCandidate: true,
  bullhornIntegration: false,
  isSchedulingEnabled: false,
  hasLicense: true,
  customerWhatsAppEnabled: false,
  smsDisabled: false,
  whatsAppEnabled: false,
  whatsAppNumberType: '',
  whatsAppBusinessAccountId: '',
  whatsAppSelfServiceIntegrationStatus: '',
  userTermsOfServiceRecords: [],
  isLoading: false,
  isFetchingAtsUser: false,
  isIngestingAtsUser: false,
  availabilitySuccess: false,
  timeZone: 'America/Los_Angeles',
  users: [],
  enableEuRegion: false,
  atsUser: {},
  atsUserDetails: {},
  ingestedAtsUser: {},
  ingestedUserDetails: {},
  page: 1,
  usersCount: 0,
  resetLinkSent: false,
  isICIMSTECalendarSwitched: false,
  apiMappingList: [],
  facebookUserMaps: null,
  facebookConnections: [],
  backgroundEvents: {
    events: [],
    page: 1,
    eventsCount: 0,
    // isLoading was added to handle state transition and we realized it's no longer
    // needed. And it's left here to prevent app from breaking since SB child component
    // depends on it. -Amir
    isLoading: true,
    isRowsPerPageChanged: false,
  },
  deletedUserDetails: {
    firstName: '',
    lastName: '',
  },
  isProfileUpdated: false,
  userDetails: {
    fullName: '',
    phoneNumber: '',
    smsProvider: '',
    atsPersonId: '',
    role: '',
    linkText: '',
    id: '',
    userName: '',
    status: '',
    firstName: '',
    lastName: '',
    inboxAccessList: [],
    addAtsPersonIdError: '',
  },
  customerInfo: {
    enableCalendarLink: true,
    enableChangePassword: true,
    enableMessageSettings: true,
    enableProfileSettings: true,
    enableSendTimeSettings: true,
    enableRegulatoryBundle: false,
    eventsEnabled: true,
    groupTextingDisabled: true,
    showDigitalAssistantResponsesTable: false,
    virtualCareerFairEnabled: true,
    userCanManageUserTemplates: true,
    userCanManageOptOut: true,
    templateFreeTextDisabled: true,
    canAddCandidate: true,
    emailIntegrationEndpoint: '',
    weChatStatus: '',
    blockNewCredits: false,
    internalCustomerType: '',
    isFlaiCustomer: false,
    twilioMappedCustomerId: '',
  },
  userInbox: {
    status: '',
    errorMessage: '',
    inboxAccessList: [],
  },
  savingUser: false,
  isUserDetailsLoading: false,
  userDeleted: false,
  isUserActionPending: false,
  userByIdError: false,
  messageTemplate: {
    data: [],
    error: {},
    loading: false,
  },
  messageTemplateCount: 0,
  messageTempateSaved: false,
  deleteMessageTemplateStatus: false,
  messageTemplateEditStatus: false,
  templateSaveError: null,
  templateDeleteError: null,
  isAddUserInboxPending: false,
  isRemoveUserInboxPending: false,
  profileSettingsUpdated: false,
  bookingsUpdated: false,
  bookingsUpdatedLoading: false,
  usersOmniList: [],
  isUsersOmniLoading: false,
  isAPIError: false,
  sendAsUserDetails: {},
  sendAsUserFetched: false,
  isSendAsUserDetailsLoading: false,
};

const getResourceLink = (user) => {
  if (!user || !user.resourceLinkList) {
    return {};
  }

  let resourceLink = {};

  if (user.resourceLinkList.length > 0) {
    resourceLink = user.resourceLinkList.find(
      (link) =>
        link.internalName === productTypes.TEXT_RECRUIT &&
        link.role &&
        link.role !== roles.SYSTEM_ADMIN,
    );
  }

  return resourceLink || {};
};

const getUserDetails = (user) => {
  const { atsPersonId = '' } = user;

  const { smsPhoneNumber, smsProvider, role, linkText, teamName, team, teamId } =
    getResourceLink(user);

  return {
    ...user,
    fullName: `${user.firstName} ${user.lastName}`,
    phoneNumber: smsPhoneNumber,
    atsPersonId,
    smsProvider,
    role,
    linkText,
    teamName,
    team,
    teamId,
  };
};

const isNotIcimsCalendar = (calendarList) =>
  Boolean(
    calendarList.filter((item) =>
      [calendarTypes.GOOGLE, calendarTypes.MICROSOFT, calendarTypes.EXCHANGE].includes(
        (item.name || '').toLowerCase(),
      ),
    ).length,
  );
/* When user has Icims calendar , api mapping List array can be empty or will not have any of google, msft, exchange calendars */

const changeOrRecoverPasswordPending = (state) => {
  state.passwordUpdated = false;
  state.isPasswordSameAsPrevious = false;
  state.showInvalidLink = false;
  state.isCurrentPasswordError = false;
};

const changeOrRecoverPassword = (state) => {
  state.passwordUpdated = true;
  state.isPasswordSameAsPrevious = false;
  state.showInvalidLink = false;
};

const updateFacebookConnections = (state, action) => {
  const facebookUserMap = JSON.parse(action.payload.FacebookUserMap);
  const { facebookConnections } = state;
  const connections = facebookConnections.map((connection) =>
    connection.id === facebookUserMap.id ? facebookUserMap : connection,
  );
  state.facebookConnections = connections;
  [state.facebookUserMaps] = connections;
};
const getSmsPhoneNumberAndWhatsAppFlag = (customerIds) => {
  const r = { smsPhoneNumber: null, whatsAppEnabled: false };
  (customerIds || []).forEach((customerId) => {
    if (customerId.product === productTypes.TEXT_RECRUIT) {
      if (customerId.smsPhoneNumber) {
        r.smsPhoneNumber = customerId.smsPhoneNumber;
        r.whatsAppEnabled = customerId.whatsAppEnabled;
        r.smsProvider = customerId.smsProvider;
        r.whatsAppNumberType = customerId.whatsAppNumberType;
        r.whatsAppSelfServiceIntegrationStatus = customerId.twilioWhatsAppSenderStatus;
        r.whatsAppBusinessAccountId = customerId.whatsAppBusinessAccountId;
      }
    }
  });
  return r;
};

const handleWhatsAppResponse = (resourceLinkList, state) => {
  const {
    smsPhoneNumber,
    whatsAppEnabled,
    whatsAppNumberType,
    twilioWhatsAppSenderStatus,
    smsProvider,
  } = resourceLinkList.find((item) => item.name === 'TextRecruit') || {};
  state.whatsAppEnabled = Boolean(whatsAppEnabled);
  state.whatsAppNumberType = whatsAppNumberType;
  state.whatsAppSelfServiceIntegrationStatus = twilioWhatsAppSenderStatus;
  state.smsPhoneNumber = smsPhoneNumber;
  state.smsProvider = smsProvider;
};

// adding below condition check to make sure whether we are getting timezone value and that timezone is present in the list.

const allAvailableTimezones = [...timeZones, ...extendedTimeZones];

const checkUserTimeZoneInAvailableList = (timeZone) =>
  Boolean(allAvailableTimezones.filter((item) => item.value === timeZone).length);

const getUserTimeZone = (timeZone) => {
  if (timeZone && checkUserTimeZoneInAvailableList(timeZone)) {
    return timeZone;
  }
  // if we didn't find timeZone in our list , guess the user timezone and find it in guess list and return timeZone
  const userGuessedTimeZone = moment.tz.guess();
  if (timeZoneMap[userGuessedTimeZone]) {
    return timeZoneMap[userGuessedTimeZone];
  }
  if (extendedTimeZoneMap[userGuessedTimeZone]) {
    return extendedTimeZoneMap[userGuessedTimeZone];
  }

  // If we didn't find timeZone in any of the above process return default timeZone
  return timeZones[0].value;
};

const processAvailability = (workingHours) =>
  workingHours.map((data) => ({
    day: data.day,
    start: data.start.$date,
    end: data.end.$date,
  }));

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},

  // eslint-disable-next-line max-statements
  extraReducers: (builder) => {
    builder.addCase(setShowScreen, (state, action) => {
      state.showScreen = action.payload;
    });

    builder.addCase(setShowInvalidLink, (state, action) => {
      state.showInvalidLink = action.payload;
    });

    builder.addCase(setUsername, (state, action) => {
      state.username = action.payload;
    });

    builder.addCase(setPassword, (state, action) => {
      state.password = action.payload;
    });

    builder.addCase(setForgotPasswordState, (state, action) => {
      state.resetLinkSent = action.payload;
    });

    builder.addCase(passwordAndScreenUpdates, (state, action) => {
      state.passwordUpdated = action.payload.passwordUpdate;
      state.showScreen = action.payload.screen;
    });

    builder.addCase(setIsInternal, (state, action) => {
      const { username } = action.payload;
      state.isInternal = username.includes('@icims') || username.includes('@textrecruit');
    });

    builder.addCase(lookupRegion.fulfilled, (state, action) => {
      const { providers = [], _enableEuRegion = false } = action.payload;
      const filteredProviders = filterProviders(providers, EU_HOSTED, _enableEuRegion);

      state.cloudEnvironments = filterCloudEnvironments(providers, EU_HOSTED, _enableEuRegion);
      state.authProviders = filteredProviders;
      state.auth0Provider = filteredProviders.find((auth) => auth.authType === 'AUTH0');
    });

    builder.addCase(sso.pending, (state) => {
      state.isLoadingRemoteAuths = true;
      state.remoteAuths = [];
    });

    builder.addCase(sso.fulfilled, (state, action) => {
      const remoteAuths = action.payload.remoteAuths || [];
      state.isLoadingRemoteAuths = false;
      state.remoteAuths = remoteAuths;
      if (remoteAuths.length) {
        state.authType = authTypes.SSO;
      }
    });

    builder.addCase(sso.rejected, (state) => {
      state.isLoadingRemoteAuths = false;
      state.remoteAuths = [];
    });

    builder.addCase(forgotPassword.pending, (state) => {
      state.resetLinkSent = false;
    });

    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.resetLinkSent = true;
    });

    builder.addCase(forgotPassword.rejected, (state, action) => {
      state.resetLinkSent = false;
      state.error = {
        message: action.payload.message,
      };
    });

    builder.addCase(verifyPassword.rejected, (state, action) => {
      if (action.payload.message === 'Request ID is invalid/used/expired') {
        state.showInvalidLink = true;
      }
    });

    builder.addCase(recoverPassword.pending, (state) => changeOrRecoverPasswordPending(state));
    builder.addCase(recoverPassword.fulfilled, (state) => changeOrRecoverPassword(state));

    builder.addCase(recoverPassword.rejected, (state, action) => {
      state.passwordUpdated = false;
      if (action.payload.message === 'You cannot use your prior 5 passwords') {
        state.isPasswordSameAsPrevious = true;
      }
      if (action.payload.message === 'Request ID is invalid/used/expired') {
        state.showInvalidLink = true;
      }
    });

    builder.addCase(changePassword.pending, (state) => changeOrRecoverPasswordPending(state));
    builder.addCase(changePassword.fulfilled, (state) => changeOrRecoverPassword(state));

    builder.addCase(changePassword.rejected, (state, action) => {
      state.passwordUpdated = false;
      if (action.payload.message === 'New password cannot be the same as the old password') {
        state.isPasswordSameAsPrevious = true;
      }
      if (action.payload.message === 'You cannot use your prior 5 passwords') {
        state.isUniquePasswordError = true;
      }
      if (action.payload.message === 'Bad username/password') {
        state.isCurrentPasswordError = true;
      }
    });

    builder.addCase(login.pending, (state) => {
      state.authorizing = true;
      state.isLoading = true;
    });

    builder.addCase(login.fulfilled, (state, action) => {
      state.authorizing = false;
      state.id = action.payload.id;
      state.firstName = action.payload.firstName;
      state.lastName = action.payload.lastName;
      state.isLoading = false;
      state.userName = action.payload.userName;
      state.timeZone = getUserTimeZone(action.payload.timeZone);
    });

    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      state.error = {
        message: action.payload.message,
      };
      if (action.payload.message === 'Your password has expired') {
        state.showScreen = 'expiredPassword';
      }
    });

    builder.addCase(resetLoginError, (state) => {
      state.error = {};
    });

    builder.addCase(availability.fulfilled, (state, action) => {
      state.availabilitySuccess = true;
      state.availability = action.payload.availability;
    });

    builder.addCase(updateBookingSettings.fulfilled, (state, action) => {
      state.bookingsUpdated = true;
      state.bookingsUpdatedLoading = false;
      state.bookingDuration = action.payload.bookingDuration;
      state.bookingDelay = action.payload.bookingDelay;
      state.delayBookingToNextDay = action.payload.delayBookingToNextDay;
    });

    builder.addCase(updateBookingSettings.rejected, (state) => {
      state.bookingsUpdatedLoading = false;
    });

    builder.addCase(updateBookingSettings.pending, (state) => {
      state.bookingsUpdatedLoading = true;
    });

    builder.addCase(updateAvailibility.fulfilled, (state, action) => {
      state.availabilityUpdated = true;
      state.availability = action.payload.availability;
    });

    builder.addCase(disconnectCalendar.fulfilled, (state) => {
      state.isICIMSTECalendarSwitched = true;
    });

    // eslint-disable-next-line max-statements
    builder.addCase(sessionInfo.fulfilled, (state, action) => {
      const { customerInfo, userInfo, weChatStatus } = action.payload;
      const {
        smsPhoneNumber,
        whatsAppEnabled,
        whatsAppBusinessAccountId,
        whatsAppSelfServiceIntegrationStatus,
        smsProvider,
        whatsAppNumberType,
      } = getSmsPhoneNumberAndWhatsAppFlag(userInfo.customerIds);

      const apiMappingList = userInfo.apiMappingList || [];
      state.authenticated = true;
      state.apiMappingList = apiMappingList;
      const facebookUserMaps = userInfo.facebookUserMaps || [];
      state.facebookUserMaps = facebookUserMaps.length ? facebookUserMaps[0] : null;
      state.facebookConnections = facebookUserMaps;
      state.authenticated = true;
      state.id = userInfo.id;
      state.firstName = userInfo.firstName;
      state.lastName = userInfo.lastName;
      state.userName = userInfo.userName;
      state.email = userInfo.email;
      state.customerIds = userInfo.customerIds;
      state.hasLicense = Boolean(smsPhoneNumber);
      state.smsPhoneNumber = smsPhoneNumber;
      state.smsDisabled = Boolean(userInfo.smsDisabled);
      state.smsProvider = smsProvider;
      state.whatsAppEnabled = Boolean(whatsAppEnabled);
      state.whatsAppNumberType = whatsAppNumberType;
      state.whatsAppSelfServiceIntegrationStatus = whatsAppSelfServiceIntegrationStatus;
      state.whatsAppBusinessAccountId = whatsAppBusinessAccountId;
      state.inboxAccessList = userInfo.inboxAccessList;
      state.authType = userInfo.authType;
      state.activeAccount = customerInfo.currentPlan;
      state.icimsUser = customerInfo.icimsUser;
      state.atsPersonId = userInfo.atsPersonId;
      state.forwardingNumber = userInfo.forwardingNumber;
      state.forwardingEnabled = userInfo.forwardingEnabled;
      state.userTermsOfServiceRecords = userInfo.tos || [];

      state.customerInfo.canAddCandidate = customerInfo.enableCandidateAdd;
      state.customerInfo.enableCalendarLink = customerInfo.enableCalendarLink;
      state.customerInfo.enableChangePassword = customerInfo.enableChangePassword;
      state.customerInfo.enableMessageSettings = customerInfo.enableMessageSettings;
      state.customerInfo.enableProfileSettings = customerInfo.enableProfileSettings;
      state.customerInfo.enableSendTimeSettings = customerInfo.enableSendTimeSettings;
      state.customerInfo.eventsEnabled = customerInfo.eventsEnabled;
      state.customerInfo.groupTextingDisabled = customerInfo.groupTextingDisabled;
      state.customerInfo.showDigitalAssistantResponsesTable =
        customerInfo.showDigitalAssistantResponsesTable;
      state.customerInfo.virtualCareerFairEnabled = customerInfo.virtualCareerFairEnabled;
      state.customerInfo.userCanManageUserTemplates = customerInfo.userCanManageUserTemplates;
      state.customerInfo.userCanManageOptOut = customerInfo.userCanManageOptOut;
      state.customerInfo.templateFreeTextDisabled = customerInfo.templateFreeTextDisabled;
      state.customerInfo.seatLicenses = customerInfo.seatLicenses;
      state.customerInfo.usedSeatLicenses = customerInfo.usedSeatLicenses;
      state.customerInfo.authType = customerInfo.authType;
      state.customerInfo.enableRegulatoryBundle = customerInfo.enableRegulatoryBundle;
      state.customerInfo.blockNewCredits = customerInfo.blockNewCredits;
      state.customerInfo.internalCustomerType = customerInfo.internalCustomerType;
      state.customerInfo.isFlaiCustomer = customerInfo.enableFlaiIntegration;
      state.customerInfo.twilioMappedCustomerId = customerInfo.twilioMappedCustomerId || '';

      state.icimsIntegration = customerInfo.icimsIntegration;
      state.icimsCustomFieldCandidate = customerInfo.icimsCustomFieldCandidate;
      state.icimsCustomFieldJob = customerInfo.icimsCustomFieldJob;
      state.icimsCustomFieldResponseFlag = customerInfo.icimsCustomFieldResponseFlag;
      state.icimsCustomFieldWorkflow = customerInfo.icimsCustomFieldWorkflow;
      state.icimsPhonePrefix = customerInfo.icimsPhonePrefix;
      state.bullhornIntegration = customerInfo.bullhornIntegration;
      state.customerId = customerInfo.id;
      state.customerWhatsAppEnabled = Boolean(customerInfo.twilioAccountId);
      state.isSchedulingEnabled = !isNotIcimsCalendar(apiMappingList);
      state.creditsUsed = customerInfo.creditsUsed;
      state.userCreditsUsed = userInfo.creditsUsed;
      state.subscriptionRenewal = customerInfo.subscriptionRenewalDate;
      state.volume = customerInfo.volume;
      state.volumeOverride = customerInfo.volumeOverride;
      state.customerInfo.customerName = customerInfo.name;
      state.customerInfo.textRecruitSalesForceId = customerInfo.textRecruitSalesForceId || '';
      state.customerInfo.emailIntegrationEndpoint = customerInfo.emailIntegrationEndpoint;
      state.enableTeams = customerInfo.enableTeams;

      if (userInfo.availability) {
        state.availability = processAvailability(userInfo.availability);
      }

      if (customerInfo.sendInitialOptOut) {
        state.optOutMessageType = 'default';
        state.initialOptOutMsg = '';
        // Not too sure why this is needed. But this is there in the old code. so keeping the above line here. -Rahul
      }
      if (customerInfo.initialOptOutMsg) {
        state.initialOptOutMsg = customerInfo.initialOptOutMsg;
        state.optOutMessageType = 'custom';
      } else {
        state.optOutMessageType = 'default';
        state.initialOptOutMsg = '';
      }
      state.customerWideOptOut = {
        sendInitialOptOut: customerInfo.sendInitialOptOut,
        initialOptOutMsg: customerInfo.initialOptOutMsg,
      };
      state.sendTimeRestriction = customerInfo.sendTimeRestriction;
      state.bookingDelay = userInfo.bookingDelay;
      state.bookingDuration = userInfo.bookingDuration;
      state.delayBookingToNextDay = userInfo.delayBookingToNextDay;
      state.weChatStatus = weChatStatus;
    });

    builder.addCase(sessionInfo.rejected, (state, action) => {
      state.error = {
        message: action.payload.message,
      };
    });

    builder.addCase(fetchTokenInfo.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(fetchTokenInfo.fulfilled, (state, action) => {
      const {
        id,
        firstName,
        lastName,
        userName,
        signupPhone,
        email,
        customerIds,
        inboxAccessList,
        smsDisabled,
        voicemailText,
        voicemailDisabled,
        autoReplyTextMsg,
        forwardingEnabled,
        forwardingNumber,
        duplicateSendCheckFlag,
        unreadMessageNotificationType,
        initialOptOutMsg,
        timeZone,
        token,
        ewsIntegration,
        tos,
      } = action.payload;

      const apiMappingList = action.payload.apiMappingList || [];
      state.apiMappingList = apiMappingList;
      const { smsPhoneNumber, whatsAppEnabled } = getSmsPhoneNumberAndWhatsAppFlag(customerIds);

      state.authenticated = true;
      state.id = id;
      state.firstName = firstName;
      state.lastName = lastName;
      state.signupPhone = signupPhone;
      state.timeZone = getUserTimeZone(timeZone);
      state.userName = userName;
      state.token = token;
      state.email = email.toLowerCase();
      state.customerIds = customerIds;
      state.hasLicense = Boolean(smsPhoneNumber);
      state.smsPhoneNumber = smsPhoneNumber;
      state.smsDisabled = Boolean(smsDisabled);
      state.whatsAppEnabled = Boolean(whatsAppEnabled);
      state.isSchedulingEnabled = !isNotIcimsCalendar(apiMappingList);
      state.inboxAccessList = inboxAccessList;
      state.voicemailDisabled = voicemailDisabled;
      state.voicemailText = voicemailText;
      state.autoReplyTextMsg = autoReplyTextMsg;
      state.forwardingEnabled = forwardingEnabled;
      state.forwardingNumber = forwardingNumber;
      state.duplicateSendCheckFlag = duplicateSendCheckFlag;
      state.unreadMessageNotificationsType = unreadMessageNotificationType;
      state.optOutMsg = initialOptOutMsg;
      state.ewsIntegration = ewsIntegration;
      state.isICIMSTECalendarSwitched = false;
      state.isLoading = false;
      state.userTermsOfServiceRecords = tos || [];
    });

    builder.addCase(usersListInfo.pending, (state) => {
      state.isLoading = true;
    });

    builder.addCase(usersListInfo.fulfilled, (state, action) => {
      const { page, items } = action.payload.paging;
      state.isLoading = false;
      state.page = page;
      state.usersCount = items;
      state.users = action.payload.users || [];
      state.userByIdError = false;
    });

    builder.addCase(usersListInfo.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload.error;
      state.userByIdError = false;
    });

    builder.addCase(fetchBackgroundEvents.fulfilled, (state, action) => {
      const events = action.payload.events || [];
      if (events.length && events[0].type === backgroundEventTypes.USER_IMPORT) {
        commonStateUpdates(state, action);
      } else {
        const { page, items } = action.payload.paging;
        state.backgroundEvents.isRowsPerPageChanged = false;
        state.backgroundEvents.events = action.payload.events;
        state.backgroundEvents.page = page;
        state.backgroundEvents.eventsCount = items;
        state.backgroundEvents.isLoading = false;
      }
    });

    builder.addCase(fetchBackgroundEvents.rejected, (state, action) => {
      state.backgroundEvents.error = action.payload.error;
    });

    builder.addCase(fetchBackgroundEvents.pending, (state) => {
      if (state.backgroundEvents.isRowsPerPageChanged) {
        state.backgroundEvents.isLoading = true;
      }
    });

    builder.addCase(setIsRowsPerPageChanged, (state) => {
      state.backgroundEvents.isRowsPerPageChanged = true;
    });

    builder.addCase(updateProfileSettings.pending, (state) => {
      state.profileSettingsUpdated = false;
      state.isProfileUpdated = true;
    });

    builder.addCase(updateProfileSettings.fulfilled, (state, action) => {
      const {
        id,
        firstName,
        lastName,
        signupPhone,
        timeZone,
        userName,
        email,
        customerIds,
        voicemailText,
        voicemailDisabled,
        autoReplyTextMsg,
        forwardingEnabled,
        forwardingNumber,
        duplicateSendCheckFlag,
        unreadMessageNotificationType,
        initialOptOutMsg,
      } = action.payload;

      const { smsPhoneNumber } = getSmsPhoneNumberAndWhatsAppFlag(customerIds);

      state.id = id;
      state.firstName = firstName;
      state.lastName = lastName;
      state.timeZone = getUserTimeZone(timeZone);
      state.signupPhone = signupPhone;
      state.userName = userName;
      state.email = email.toLowerCase();
      state.customerIds = customerIds;
      state.hasLicense = Boolean(smsPhoneNumber);
      state.smsPhoneNumber = smsPhoneNumber;
      state.voicemailDisabled = voicemailDisabled;
      state.voicemailText = voicemailText;
      state.autoReplyTextMsg = autoReplyTextMsg;
      state.forwardingEnabled = forwardingEnabled;
      state.forwardingNumber = forwardingNumber;
      state.duplicateSendCheckFlag = duplicateSendCheckFlag;
      state.unreadMessageNotificationsType = unreadMessageNotificationType;
      state.optOutMsg = initialOptOutMsg;
      state.profileSettingsUpdated = true;
      state.isProfileUpdated = false;
    });

    builder.addCase(updateProfileSettings.rejected, (state) => {
      state.profileSettingsUpdated = false;
      state.isProfileUpdated = false;
    });

    builder.addCase(fetchAddUser.pending, (state) => {
      state.savingUser = true;
      state.userSaved = false;
      state.userByIdError = false;
    });

    builder.addCase(fetchAddUser.fulfilled, (state, action) => {
      state.savingUser = false;
      state.userDetails = getUserDetails(action.payload.user);
      state.userDetails.id = action.payload.user.id;
      state.userSaved = true;
      state.userByIdError = false;
    });

    builder.addCase(fetchAddUser.rejected, (state) => {
      state.savingUser = false;
      state.userSaved = false;
      state.userByIdError = true;
    });

    builder.addCase(fetchUpdateUser.pending, (state) => {
      state.savingUser = true;
      state.userUpdated = false;
      state.userByIdError = false;
    });

    builder.addCase(fetchUpdateUser.fulfilled, (state, action) => {
      state.savingUser = false;
      state.userDetails = getUserDetails(action.payload.user);
      state.userUpdated = true;
      state.userByIdError = false;
    });

    builder.addCase(fetchUpdateUser.rejected, (state, action) => {
      state.savingUser = false;
      state.userUpdated = false;
      state.updateUserError = action.payload.error;
      state.userByIdError = false;
    });

    builder.addCase(fetchUserDetails.pending, (state) => {
      state.isUserDetailsLoading = true;
      state.userDetails.error = false;
    });

    builder.addCase(fetchUserDetails.fulfilled, (state, action) => {
      state.userDetails = getUserDetails(action.payload.user);
      state.isUserDetailsLoading = false;
      state.userDetails.error = false;
    });

    builder.addCase(fetchUserDetails.rejected, (state, action) => {
      state.userDetails.error = action.payload.message;
      state.isUserDetailsLoading = false;
    });

    builder.addCase(fetchSendAsUserDetails.pending, (state) => {
      state.isSendAsUserDetailsLoading = true;
      state.sendAsUserDetails.error = false;
      state.sendAsUserFetched = false;
    });

    builder.addCase(fetchSendAsUserDetails.fulfilled, (state, action) => {
      const response = action.payload;
      state.sendAsUserDetails.fullName = `${response.firstName} ${response.lastName}`;
      state.sendAsUserDetails.userName = response.userName;
      state.isSendAsUserDetailsLoading = false;
      state.sendAsUserDetails.error = false;
      state.sendAsUserFetched = true;
    });

    builder.addCase(fetchSendAsUserDetails.rejected, (state, action) => {
      state.sendAsUserDetails.error = action.payload.message;
      state.isSendAsUserDetailsLoading = false;
      state.sendAsUserFetched = false;
    });

    builder.addCase(resetSendAsUser, (state) => {
      state.sendAsUserDetails = {};
      state.sendAsUserFetched = false;
    });

    builder.addCase(importAtsUser.pending, (state) => {
      state.isIngestingAtsUser = true;
      state.ingestedUserDetails.error = false;
    });

    builder.addCase(importAtsUser.fulfilled, (state, action) => {
      state.ingestedAtsUser = action.payload.user;
      state.isIngestingAtsUser = false;
      state.ingestedUserDetails.error = false;
    });

    builder.addCase(importAtsUser.rejected, (state, action) => {
      /*
				The backend provides a long string that is not properly formatted.
				Below is a temporary solution to extract the error object.
				For more info see: TXTE-22281. -Elpi
			*/
      const [error] = action.payload.message.match(/{.*}/) || [];
      if (error) {
        const { errors } = JSON.parse(error);
        const [errorObj] = errors;
        const { errorMessage } = errorObj;
        state.ingestedUserDetails.error = errorMessage;
      } else {
        state.ingestedUserDetails.genericError = true;
      }
      state.isIngestingAtsUser = false;
    });

    builder.addCase(resetUserSave, (state) => {
      state.savingUser = false;
      state.userSaved = false;
      state.userUpdated = false;
    });

    builder.addCase(expirePassword.pending, (state) => {
      state.isUserActionPending = true;
    });

    builder.addCase(expirePassword.fulfilled, (state, action) => {
      state.userDetails = getUserDetails(action.payload.user);
      state.isUserActionPending = false;
    });

    builder.addCase(expirePassword.rejected, (state, action) => {
      state.userDetails.error = action.payload.error;
      state.isUserActionPending = false;
    });

    builder.addCase(addUserInbox.pending, (state) => {
      state.isAddUserInboxPending = true;
    });

    builder.addCase(addUserInbox.fulfilled, (state, action) => {
      state.isAddUserInboxPending = false;
      state.userInbox.status = action.payload.apiResult.status;
      state.userDetails.inboxAccessList = action.payload.user.inboxAccessList;
    });

    builder.addCase(addUserInbox.rejected, (state, action) => {
      state.userInbox.status = action.payload.apiResult.status;
      state.userInbox.errorMessage = action.payload.apiResult.message;
      state.isAddUserInboxPending = false;
    });

    builder.addCase(removeUserInbox.pending, (state) => {
      state.isRemoveUserInboxPending = true;
    });

    builder.addCase(removeUserInbox.fulfilled, (state, action) => {
      state.isRemoveUserInboxPending = false;
      state.userInbox.status = action.payload.apiResult.status;
      state.userDetails.inboxAccessList = action.payload.user.inboxAccessList;
    });

    builder.addCase(removeUserInbox.rejected, (state, action) => {
      state.userInbox.status = action.payload.apiResult.status;
      state.userInbox.errorMessage = action.payload.apiResult.message;
      state.isRemoveUserInboxPending = false;
    });

    builder.addCase(addLicense.pending, (state) => {
      state.isUserActionPending = true;
    });

    builder.addCase(addLicense.fulfilled, (state, action) => {
      state.userDetails = getUserDetails(action.payload.user);
      state.isUserActionPending = false;
    });

    builder.addCase(addLicense.rejected, (state, action) => {
      state.userDetails.error = action.payload.error;
      state.isUserActionPending = false;
    });

    builder.addCase(removeLicense.pending, (state) => {
      state.isUserActionPending = true;
    });

    builder.addCase(removeLicense.fulfilled, (state, action) => {
      state.userDetails = getUserDetails(action.payload.user);
      state.isUserActionPending = false;
    });

    builder.addCase(removeLicense.rejected, (state, action) => {
      state.userDetails.error = action.payload.error;
      state.isUserActionPending = false;
    });

    builder.addCase(addAtsPersonId.pending, (state) => {
      state.isUserActionPending = true;
      state.userDetails.addAtsPersonIdError = '';
    });

    builder.addCase(addAtsPersonId.fulfilled, (state, action) => {
      const userDetails = getUserDetails(action.payload.user);

      state.userDetails = { ...userDetails };
      state.isUserActionPending = false;
      state.atsPersonId = userDetails.atsPersonId;
    });

    builder.addCase(addAtsPersonId.rejected, (state, action) => {
      state.userDetails.addAtsPersonIdError = action.error.message;
      state.isUserActionPending = false;
    });

    builder.addCase(removeAtsPersonId.pending, (state) => {
      state.isUserActionPending = true;
    });

    builder.addCase(removeAtsPersonId.fulfilled, (state, action) => {
      const userDetails = getUserDetails(action.payload.user);

      state.userDetails = { ...userDetails };
      state.atsPersonId = userDetails.atsPersonId;
      state.isUserActionPending = false;
    });

    builder.addCase(removeAtsPersonId.rejected, (state, action) => {
      state.userDetails.error = action.payload.error;
      state.isUserActionPending = false;
    });

    builder.addCase(updateDisableUser.pending, (state) => {
      state.userStatusUpdated = false;
      state.isUserActionPending = true;
    });

    builder.addCase(updateDisableUser.fulfilled, (state, action) => {
      state.userDetails = getUserDetails(action.payload.user);
      state.userStatusUpdated = true;
      state.isUserActionPending = false;
    });

    builder.addCase(updateDisableUser.rejected, (state) => {
      state.userStatusUpdated = false;
      state.isUserActionPending = false;
    });

    builder.addCase(fetchDeleteUser.pending, (state) => {
      state.userStatusUpdated = false;
      state.userDeleted = false;
    });

    builder.addCase(fetchDeleteUser.fulfilled, (state, action) => {
      state.deletedUserDetails.firstName = action.payload.firstName;
      state.deletedUserDetails.lastName = action.payload.lastName;
      state.userStatusUpdated = true;
      state.userDeleted = true;
    });

    builder.addCase(fetchAtsUser.pending, (state) => {
      state.isFetchingAtsUser = true;
      state.atsUserDetails.error = false;
    });

    builder.addCase(fetchAtsUser.fulfilled, (state, action) => {
      state.atsUser = action.payload.person;
      state.isFetchingAtsUser = false;
      state.atsUserDetails.error = false;
    });

    builder.addCase(fetchAtsUser.rejected, (state, action) => {
      state.atsUserDetails.error = action.payload.message;
      state.isFetchingAtsUser = false;
    });

    builder.addCase(resetAtsUser, (state) => {
      state.atsUser = {};
      state.atsUserDetails = {};
      state.ingestedAtsUser = {};
      state.ingestedUserDetails = {};
    });

    builder.addCase(fetchDeleteUser.rejected, (state) => {
      state.userStatusUpdated = false;
      state.userDeleted = false;
    });

    builder.addCase(handleSnackClose, (state) => {
      state.userDeleted = false;
    });

    builder.addCase(fetchMessageTemplate.pending, (state) => {
      state.messageTemplate.loading = true;
    });

    builder.addCase(fetchMessageTemplate.fulfilled, (state, action) => {
      state.messageTemplate.data = action.payload.templates;
      state.messageTemplate.error = false;
      state.messageTemplate.loading = false;
      state.messageTemplateCount = action.payload.paging.items;
    });

    builder.addCase(fetchMessageTemplate.rejected, (state, action) => {
      state.messageTemplate.error = action.payload.error;
      state.messageTemplate.loading = false;
    });

    builder.addCase(saveMessageTemplateInbound.rejected, (state, action) => {
      state.messageTempateSaved = false;
      state.templateSaveError = action.payload?.message || 'Failed to save template';
    });

    builder.addCase(saveMessageTemplateInbound.fulfilled, (state) => {
      state.messageTempateSaved = true;
      state.templateSaveError = null;
    });

    builder.addCase(saveMessageTemplateInbound.pending, (state) => {
      state.messageTempateSaved = false;
      state.templateSaveError = null;
    });

    builder.addCase(deleteMessageTemplate.rejected, (state, action) => {
      state.deleteMessageTemplateStatus = false;
      state.templateDeleteError = action.payload?.message || 'Failed to delete template';
    });

    builder.addCase(deleteMessageTemplate.fulfilled, (state) => {
      state.deleteMessageTemplateStatus = true;
      state.templateDeleteError = null;
    });

    builder.addCase(deleteMessageTemplate.pending, (state) => {
      state.deleteMessageTemplateStatus = false;
      state.templateDeleteError = null;
    });

    builder.addCase(setMessageTemplateIsEdittrue, (state) => {
      state.messageTemplateEditStatus = true;
    });

    builder.addCase(resetMessageTemplateSaveStatus, (state) => {
      state.deleteMessageTemplateStatus = false;
      state.messageTempateSaved = false;
      state.messageTemplateEditStatus = false;
      state.templateSaveError = null;
      state.templateDeleteError = null;
    });

    builder.addCase(fetchResetPassword.pending, (state) => {
      state.userStatusUpdated = false;
    });

    builder.addCase(fetchResetPassword.fulfilled, (state) => {
      state.userStatusUpdated = true;
    });

    builder.addCase(fetchResetPassword.rejected, (state) => {
      state.userStatusUpdated = false;
    });

    builder.addCase(resetProfileUpdateStatus, (state) => {
      state.profileSettingsUpdated = false;
    });

    builder.addCase(setAPIError, (state, action) => {
      state.isAPIError = action.payload;
    });

    builder.addCase(addFacebook.fulfilled, (state, action) => {
      const facebookUserMap = JSON.parse(action.payload.FacebookUserMap);
      state.facebookConnections = [...state.facebookConnections, facebookUserMap];
      [state.facebookUserMaps] = state.facebookConnections;
    });

    builder.addCase(updateFacebook.fulfilled, (state, action) =>
      updateFacebookConnections(state, action),
    );

    builder.addCase(refreshFacebook.fulfilled, (state, action) =>
      updateFacebookConnections(state, action),
    );

    builder.addCase(disconnectFacebookUpdate, (state, action) => {
      const { facebookConnections } = state;
      const filteredFacebookConnections = facebookConnections.filter(
        (connection) => connection.id !== action.payload.id,
      );
      state.facebookConnections = filteredFacebookConnections;
      // FacebookUserMaps is used to enable and disable facebook in message composer view for the user
      state.facebookUserMaps = filteredFacebookConnections.length
        ? filteredFacebookConnections[0]
        : null;
    });
    builder.addCase(saveWhatsappConnectionResponse.fulfilled, (state, action) => {
      const { resourceLinkList } = action.payload.user;
      handleWhatsAppResponse(resourceLinkList, state);
    });
    builder.addCase(disconnectWhatsAppBusinessAccount.fulfilled, (state, action) => {
      const { resourceLinkList } = action.payload.user;
      handleWhatsAppResponse(resourceLinkList, state);
    });
    builder.addCase(resetWSSBusinessData.fulfilled, (state, action) => {
      const { resourceLinkList } = action.payload.user;
      handleWhatsAppResponse(resourceLinkList, state);
    });
    builder.addCase(verifyWhatsAppNumber.fulfilled, (state, action) => {
      const { resourceLinkList } = action.payload.user;
      handleWhatsAppResponse(resourceLinkList, state);
    });
  },
});

export default userSlice;
